import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';

export const EditModal = ({ 
  isOpen, 
  onClose, 
  calendarName,
  workdays, 
  workingHours,
  holidays, 
  onSave 
}) => {
  const [localName, setLocalName] = useState('');
  const [localWorkdays, setLocalWorkdays] = useState([]);
  const [localStartTime, setLocalStartTime] = useState('09:00');
  const [localEndTime, setLocalEndTime] = useState('17:00');
  const [localHolidays, setLocalHolidays] = useState([]);
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayName, setNewHolidayName] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLocalName(calendarName || '');
      setLocalWorkdays(workdays || []);
      setLocalStartTime(workingHours?.start || '09:00');
      setLocalEndTime(workingHours?.end || '17:00');
      setLocalHolidays(holidays || []);
    }
  }, [isOpen, calendarName, workdays, workingHours, holidays]);

  const handleWorkdayToggle = (day) => {
    if (localWorkdays.includes(day)) {
      setLocalWorkdays(localWorkdays.filter(d => d !== day));
    } else {
      setLocalWorkdays([...localWorkdays, day]);
    }
  };

  const handleAddHoliday = () => {
    if (!newHolidayDate) {
      toast.error('Please select a date');
      return;
    }

    const holidayName = newHolidayName.trim() || 'Holiday';
    
    if (localHolidays.some(h => h.date === newHolidayDate)) {
      toast.error('This date is already marked as a holiday');
      return;
    }

    setLocalHolidays([...localHolidays, { date: newHolidayDate, name: holidayName }]);
    setNewHolidayDate('');
    setNewHolidayName('');
  };

  const handleDeleteHoliday = (date) => {
    setLocalHolidays(localHolidays.filter(h => h.date !== date));
  };

  const handleSubmit = () => {
    onSave({
      name: localName,
      workdays: localWorkdays,
      workingHours: { start: localStartTime, end: localEndTime },
      holidays: localHolidays
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4 bg-[#1e3a8a] text-white p-4 -mx-6 -mt-6 rounded-t-lg">
          <h2 className="text-xl font-bold">Edit Calendar Settings</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Calendar Name</label>
            <input
              type="text"
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="Calendar Name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Working Days</label>
            <div className="flex flex-wrap gap-2">
              {[
                { day: 1, label: 'Mon' },
                { day: 2, label: 'Tue' },
                { day: 3, label: 'Wed' },
                { day: 4, label: 'Thu' },
                { day: 5, label: 'Fri' },
                { day: 6, label: 'Sat' },
                { day: 7, label: 'Sun' }
              ].map(({ day, label }) => (
                <button
                  key={day}
                  type="button"
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    localWorkdays.includes(day)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                  onClick={() => handleWorkdayToggle(day)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Working Hours</label>
            <div className="flex items-center space-x-2">
              <input
                type="time"
                value={localStartTime}
                onChange={(e) => setLocalStartTime(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2"
              />
              <span>to</span>
              <input
                type="time"
                value={localEndTime}
                onChange={(e) => setLocalEndTime(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Holidays</label>
            <div className="mb-2">
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  value={newHolidayDate}
                  onChange={(e) => setNewHolidayDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2"
                />
                <input
                  type="text"
                  value={newHolidayName}
                  onChange={(e) => setNewHolidayName(e.target.value)}
                  placeholder="Holiday Name"
                  className="border border-gray-300 rounded-md px-3 py-2"
                />
                <button
                  type="button"
                  onClick={handleAddHoliday}
                  className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {localHolidays.sort((a, b) => a.date.localeCompare(b.date)).map(holiday => (
                <div key={holiday.date} className="flex justify-between items-center bg-gray-100 p-2 rounded-md">
                  <div>
                    <div className="font-medium">{new Date(holiday.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                    <div className="text-sm text-gray-600">{holiday.name}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteHoliday(holiday.date)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {localHolidays.length === 0 && (
                <div className="text-gray-500 italic text-sm">No holidays set</div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-4 py-2 bg-[#1e3a8a] text-white rounded-md hover:bg-[#1e3a8a]/90"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AddProjectModal = ({ isOpen, onClose, onAddProject }) => {
  const [projectName, setProjectName] = useState('');

  const handleSubmit = () => {
    if (!projectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }
    onAddProject(projectName);
    setProjectName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4 bg-[#1e3a8a] text-white p-4 -mx-6 -mt-6 rounded-t-lg">
          <h2 className="text-xl font-bold">Add New Project</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="Enter project name"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-4 py-2 bg-[#1e3a8a] text-white rounded-md hover:bg-[#1e3a8a]/90"
            >
              Add Project
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AddHolidayModal = ({ 
  isOpen, 
  onClose, 
  onAddHoliday, 
  onDeleteHoliday,
  calendarId,
  selectedDate,
  existingHoliday 
}) => {
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (existingHoliday) {
      setName(existingHoliday.name);
      setDate(existingHoliday.date);
    } else {
      setName('');
      setDate(selectedDate || '');
    }
  }, [existingHoliday, selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !date) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await onAddHoliday(date, name, existingHoliday?.id);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!existingHoliday?.id) {
      onClose();
      return;
    }

    // Removed the confirmation alert and directly proceed with deletion
    await onDeleteHoliday(existingHoliday.id);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {existingHoliday ? 'Edit Holiday' : 'Add Holiday'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Holiday Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              placeholder="Enter holiday name"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div className="flex justify-between">
            <div>
              {existingHoliday && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="flex items-center px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </button>
              )}
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
              >
                {isSubmitting ? 'Saving...' : existingHoliday ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};